import {
  AdminManualPointsAwardResult,
  AdminMockDonationResult,
  AdminPointsHistoryRow,
  AdminPointsLookupUser,
  AdminPointsSummary,
} from '../types';
import { appBackendApiClient } from './apiClient';

const mapSummary = (payload: any): AdminPointsSummary => ({
  totalPoints: Number(payload?.totalPoints ?? 0),
  donationPoints: Number(payload?.donationPoints ?? 0),
  streakApprovedPoints: Number(payload?.streakApprovedPoints ?? 0),
  sharePoints: Number(payload?.sharePoints ?? 0),
  legacyCirclePoints: Number(payload?.legacyCirclePoints ?? 0),
  updatedAt: payload?.updatedAt,
});

const mapHistoryRow = (row: any): AdminPointsHistoryRow => ({
  id: row?.id || '',
  awardType: row?.awardType,
  points: Number(row?.points ?? 0),
  amount:
    row?.amount !== undefined && row?.amount !== null
      ? Number(row.amount)
      : undefined,
  currency: row?.currency || undefined,
  reason: row?.reason || 'points',
  referenceType: row?.referenceType || undefined,
  referenceId: row?.referenceId || undefined,
  createdAt: row?.createdAt || new Date().toISOString(),
  createdByUserId: row?.createdByUserId || undefined,
  createdByUserEmail: row?.createdByUserEmail || undefined,
  donationId: row?.donationId || undefined,
  transactionId: row?.transactionId || undefined,
});

const fetchUserLookup = async (
  client: typeof appBackendApiClient,
  identifier: string
): Promise<{
  user: AdminPointsLookupUser;
  summary: AdminPointsSummary;
}> => {
  const trimmed = identifier.trim();
  const params = trimmed.includes('@')
    ? { email: trimmed.toLowerCase() }
    : { userId: trimmed };

  const response = await client.get('/points/admin/users/lookup', {
    params,
  });

  return {
    user: response.data.user as AdminPointsLookupUser,
    summary: mapSummary(response.data.summary),
  };
};

const fetchHistory = async (
  client: typeof appBackendApiClient,
  userId: string,
  limit?: number
): Promise<AdminPointsHistoryRow[]> => {
  const params =
    typeof limit === 'number' && Number.isFinite(limit) ? { limit } : undefined;
  const response = await client.get(`/points/admin/users/${userId}/history`, {
    params,
  });

  const rows = Array.isArray(response.data?.items) ? response.data.items : [];
  return rows.map(mapHistoryRow);
};

const submitMockDonation = async (
  client: typeof appBackendApiClient,
  input: {
    userId?: string;
    userEmail?: string;
    amount: number;
    currency?: string;
    causeId?: string;
    challengeId?: string;
    merchantReference?: string;
  }
): Promise<AdminMockDonationResult> => {
  const response = await client.post('/donations/admin/mock-success', input);
  return response.data as AdminMockDonationResult;
};

const submitManualAward = async (
  client: typeof appBackendApiClient,
  input: {
    userId: string;
    points: number;
    note?: string;
  }
): Promise<AdminManualPointsAwardResult> => {
  const response = await client.post('/points/admin/manual-awards', input);
  return response.data as AdminManualPointsAwardResult;
};

export const PointsService = {
  async lookupUser(identifier: string): Promise<{
    user: AdminPointsLookupUser;
    summary: AdminPointsSummary;
  }> {
    return fetchUserLookup(appBackendApiClient, identifier);
  },

  async fetchUserHistory(
    userId: string,
    limit?: number
  ): Promise<AdminPointsHistoryRow[]> {
    return fetchHistory(appBackendApiClient, userId, limit);
  },

  async createMockDonation(input: {
    userId?: string;
    userEmail?: string;
    amount: number;
    currency?: string;
    causeId?: string;
    challengeId?: string;
    merchantReference?: string;
  }): Promise<AdminMockDonationResult> {
    return submitMockDonation(appBackendApiClient, input);
  },

  async createManualAward(input: {
    userId: string;
    points: number;
    note?: string;
  }): Promise<AdminManualPointsAwardResult> {
    return submitManualAward(appBackendApiClient, input);
  },
};
